import { AlertTriangle, Sparkles } from "lucide-react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";

import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { ExpensiveProblem } from "@/schema/brand-os/brand-intel";

function highlightText(text: string) {
  const parts = text.split(/([+-]?\d+\.?\d*%\s*YoY)/gi);
  return parts.map((part, i) =>
    /[+-]?\d+\.?\d*%\s*YoY/i.test(part) ? (
      <span key={i} className="font-semibold text-destructive">
        {part}
      </span>
    ) : (
      part
    )
  );
}

const ExpensiveProblemCard = ({ data }: { data?: ExpensiveProblem | null }) => {
  if (!data) return <LockedCard message={message} />;

  return (
    <div className="rounded-2xl border border-warning/30 bg-warning/[0.06] p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-destructive" strokeWidth={2.5} />
          <p className="text-base font-semibold text-foreground">The Expensive Problem</p>
        </div>
        {data.ai_flagged ? (
          <div className="flex items-center gap-1 rounded-full bg-violet-100/40 p-2 text-xs font-medium text-violet-600">
            <Sparkles className="size-4" strokeWidth={2.5} />
            AI Flagged
          </div>
        ) : null}
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{highlightText(data.description)}</p>
    </div>
  );
};

const ExpensiveProblemContent = ({ status, data }: { status: QueryStatus; data?: ExpensiveProblem | null }) => {
  switch (status) {
    case "pending":
      return <Skeleton className="h-28 w-full rounded-2xl" />;
    case "error":
      return <LockedCard message={message} />;
    case "success":
      return <ExpensiveProblemCard data={data} />;
  }
};

export function ExpensiveProblemBanner() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchExpensiveProblem());
  return <ExpensiveProblemContent status={status} data={data} />;
}
